/**
 * Meeting Header Component
 * Displays meeting title, status, metadata, and action buttons
 */

import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Calendar, ArrowLeft, Video, Building2, Users, MapPin, 
  StopCircle, Mail, RefreshCw
} from 'lucide-react';

const MeetingHeader = ({
  meeting,
  onBack,
  onEndMeeting,
  onSendReminder,
  onRefresh
}) => {
  const getMeetingTypeIcon = () => {
    switch (meeting.meeting_type) {
      case 'teams': return <Video className="h-5 w-5 text-purple-600" />;
      case 'hybrid': return <Users className="h-5 w-5 text-amber-600" />;
      default: return <Building2 className="h-5 w-5 text-blue-600" />;
    }
  };

  const getStatusVariant = () => {
    switch (meeting.status) {
      case 'completed': return 'default';
      case 'in_progress': return 'secondary';
      case 'cancelled': return 'destructive';
      default: return 'outline';
    }
  };

  return (
    <div className="flex items-center justify-between" data-testid="meeting-header">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={onBack}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
        <div>
          <div className="flex items-center gap-3">
            {getMeetingTypeIcon()}
            <h1 className="text-2xl font-bold text-foreground">{meeting.title}</h1>
            <Badge variant={getStatusVariant()} className="capitalize">
              {meeting.status?.replace('_', ' ')}
            </Badge>
          </div>
          <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
            <span className="flex items-center gap-1">
              <Calendar className="h-4 w-4" />
              {meeting.scheduled_start ? 
                new Date(meeting.scheduled_start).toLocaleDateString('en-US', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                }) : 'Not scheduled'
              }
            </span>
            {meeting.location && (
              <span className="flex items-center gap-1">
                <MapPin className="h-4 w-4" />
                {meeting.location}
              </span>
            )}
            <span className="flex items-center gap-1">
              <Users className="h-4 w-4" />
              {meeting.attendees?.length || 0} attendees
            </span>
          </div>
        </div>
      </div>
      
      <div className="flex items-center gap-2">
        {meeting.status === 'in_progress' && (
          <Button variant="destructive" onClick={onEndMeeting}>
            <StopCircle className="h-4 w-4 mr-2" />
            End Meeting
          </Button>
        )}
        <Button variant="outline" onClick={onSendReminder} data-testid="send-reminder-btn">
          <Mail className="h-4 w-4 mr-2" />
          Send Reminder
        </Button>
        <Button variant="outline" onClick={onRefresh}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>
    </div>
  );
};

export default MeetingHeader;
